import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { User } from "lucide-react";

interface TeamMember {
  id: string;
  name: string;
  title: string;
  photo_url: string | null;
  display_order: number;
}

const TeamSection = () => {
  const { data: teamMembers, isLoading } = useQuery({
    queryKey: ["team-members"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_members")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as TeamMember[];
    },
  });

  if (isLoading) {
    return (
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
            Meet the Team
          </h2>
          <div className="flex flex-wrap justify-center gap-8">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="w-64 h-80 rounded-xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-foreground">
          Meet the Team
        </h2>
        <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
          The passionate individuals behind The Lost Strings
        </p>
        <div className="flex flex-wrap justify-center gap-8">
          {teamMembers && teamMembers.length > 0 ? (
            teamMembers.map((member) => (
              <Card
                key={member.id}
                className="w-64 overflow-hidden bg-card border-border hover:shadow-lg transition-shadow duration-300"
              >
                <div className="aspect-[3/4] relative bg-muted">
                  {member.photo_url ? (
                    <img
                      src={member.photo_url}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-24 h-24 text-muted-foreground/40" />
                    </div>
                  )}
                </div>
                <CardContent className="p-4 text-center">
                  <h3 className="font-semibold text-lg text-foreground">
                    {member.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">{member.title}</p>
                </CardContent>
              </Card>
            ))
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Team members coming soon!
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
